import { QueryKeys } from "@/network/queryKeys";
import { queryClient } from "@/network/queryClient";
import type {
  tMutationClientAddedArgs,
  tMutationClientMovedArgs,
  tMutationClientRemovedArgs,
  tMutationListCreatedArgs,
  tMutationListMovedArgs,
  tMutationListRemovedArgs,
  tMutationListUpdatedArgs,
} from "./types";
import type { tClientBoard, tClientBoardList } from "@/types";
import isEqual from "lodash/isEqual";
import isempty from "lodash/isEmpty";

type tBuildDefaultBoardQueryKeyArgs = {
  saloonId?: string;
  masterId?: string;
};
const buildDefaultBoardQueryKey = ({
  saloonId,
  masterId,
}: tBuildDefaultBoardQueryKeyArgs) => [
  QueryKeys.defaultClientsBoard,
  saloonId ? { saloonId } : { masterId },
];
export const ClientBoardsCacheService = {
  buildDefaultBoardQueryKey,
  invalidateAll() {
    queryClient.invalidateQueries(QueryKeys.defaultClientsBoard);
  },
  addClient(args: tMutationClientAddedArgs) {
    if (!args.is_default_board) {
      return console.log("not implemented");
    }
    queryClient.setQueryDataIfCached(
      buildDefaultBoardQueryKey({
        saloonId: args.saloon_id,
        masterId: args.master_id,
      }),
      (board: tClientBoard): tClientBoard => {
        if (!board) return board;
        return {
          ...board,
          lists: board.lists.map((l) => {
            if (l._id === args.list) {
              return {
                ...l,
                clients: [args.client_id, ...l.clients],
              };
            } else {
              return l;
            }
          }),
        };
      }
    );
  },
  removeClient(args: tMutationClientRemovedArgs) {
    queryClient.setQueryDataIfCached(
      buildDefaultBoardQueryKey({
        saloonId: args.client.saloon,
        masterId: args.client.master,
      }),
      (board: tClientBoard): tClientBoard => {
        if (!board) return board;
        return {
          ...board,
          lists: board.lists.map((l) => {
            return {
              ...l,
              clients: l.clients.filter((c) => c !== args.client._id),
            };
          }),
        };
      }
    );
  },
  replaceBoard(board: tClientBoard) {
    if (!board.is_default) {
      return console.log("not implemented");
    }
    queryClient.setQueryDataButKeepStale(
      buildDefaultBoardQueryKey({
        saloonId: board.saloon,
        masterId: board.master,
      }),
      board
    );
  },
  removeList(args: tMutationListRemovedArgs) {
    if (!args.is_default_board) {
      return console.log("not implemented");
    }
    queryClient.setQueryDataButKeepStale(
      buildDefaultBoardQueryKey({
        saloonId: args.saloon_id,
        masterId: args.master_id,
      }),
      (board: tClientBoard): tClientBoard => {
        if (!board) return board;
        return {
          ...board,
          lists: board.lists.reduce((acc, list) => {
            if (list._id !== args.list_id) {
              if (
                list._id === "default" &&
                !isempty(args.clients_moved_to_default_list)
              ) {
                list = {
                  ...list,
                  clients: [
                    ...list.clients,
                    ...args.clients_moved_to_default_list,
                  ],
                };
              }
              acc.push(list);
            }
            return acc;
          }, [] as tClientBoardList[]),
        };
      }
    );
  },
  addList(args: tMutationListCreatedArgs) {
    if (!args.is_default_board) {
      return console.log("not implemented");
    }
    queryClient.setQueryDataButKeepStale(
      buildDefaultBoardQueryKey({
        saloonId: args.saloon_id,
        masterId: args.master_id,
      }),
      (board: tClientBoard): tClientBoard => {
        if (!board) return board;
        return {
          ...board,
          lists: board.lists.concat(args.list),
        };
      }
    );
  },
  updateList(args: tMutationListUpdatedArgs) {
    if (!args.is_default_board) {
      return console.log("not implemented");
    }
    queryClient.setQueryDataButKeepStale(
      buildDefaultBoardQueryKey({
        saloonId: args.saloon_id,
        masterId: args.master_id,
      }),
      (board: tClientBoard): tClientBoard => {
        if (!board) return board;
        return {
          ...board,
          lists: board.lists.map((l) => {
            if (l._id === args.list_id) {
              return {
                ...l,
                ...args.updates,
              };
            } else {
              return l;
            }
          }),
        };
      }
    );
  },
  replaceClients(args: tMutationClientMovedArgs) {
    if (!args.is_default_board) {
      return console.log("not implemented");
    }
    const queryKey = buildDefaultBoardQueryKey({
      masterId: args.master_id,
      saloonId: args.saloon_id,
    });
    const board = queryClient.getQueryData<tClientBoard>(queryKey);
    if (!board) return;
    const srcList = board.lists.find((l) => l._id === args.src_list_id);
    const dstList = board.lists.find((l) => l._id === args.dst_list_id);
    if (!srcList || !dstList) return;
    const isSameList = srcList === dstList;
    const shouldUpdateDst = !isEqual(dstList.clients, args.dst_clients_list);
    const shouldUpdateSrc = isSameList
      ? false
      : !isEqual(srcList.clients, args.src_clients_list);
    if (!shouldUpdateSrc && !shouldUpdateDst) return;
    const updatedBoard = {
      ...board,
      lists: board.lists.map((l) => {
        if (l === srcList && shouldUpdateSrc) {
          return {
            ...l,
            clients: args.src_clients_list,
          };
        } else if (l === dstList && shouldUpdateDst) {
          return {
            ...l,
            clients: args.dst_clients_list,
          };
        }
        return l;
      }),
    };
    queryClient.setQueryDataButKeepStale(queryKey, updatedBoard);
  },
  moveList(args: tMutationListMovedArgs) {
    if (!args.is_default_board) {
      return console.log("not implemented");
    }
    const queryKey = buildDefaultBoardQueryKey({
      masterId: args.master_id,
      saloonId: args.saloon_id,
    });
    const board = queryClient.getQueryData<tClientBoard>(queryKey);
    if (!board) return;

    // first check if we even need to reorder the lists
    for (let idx = 0; idx < board.lists.length; idx++) {
      const list = board.lists[idx];
      if (list._id !== args.new_lists_order[idx]) {
        const updatedBoard = {
          ...board,
          lists: args.new_lists_order.map((id) => {
            // TODO:: probably should've used a map
            return board.lists.find((l) => l._id === id)!;
          }),
        };
        queryClient.setQueryDataButKeepStale(queryKey, updatedBoard);
        return;
      }
    }
  },
};
